import type {
  Licence,
  LicenceType,
  PersistedStripePrice,
  SanitizedBillingSite,
  SanitizedLicence,
  SanitizedStripeSource,
  SanitizedSubscription,
  Site,
  WorkflowFreeTrialStatus,
  WorkflowsTrialSettings,
} from '@sine/sine-core-api-types';
import type { ISelectOption } from '@sine/sine-ui-components';

import { type AddonType, type ProductIconBadgeText } from '../../team/billingV2/constants/billing.constants';
import type { SiteStatistics } from '../reporting/reporting.types';

export interface HydratedBillableSite extends SanitizedBillingSite, SiteStatistics {
  /**
   * Used only by the forms to track which sites were added before sending to API.
   */
  isNew?: true;
}

export type Country = 'us' | 'au' | 'nz' | 'gb';
export type Currency = 'usd' | 'aud' | 'nzd' | 'gbp';

export enum BillingMode {
  OFF = 'off',
  CREDIT_CARD = 'credit_card',
}

export interface BadgeInfo {
  badgeName: string;
  count: number;
  licenseIds: Array<string>;
  subscriptionId: string;
}

export interface LicenceAddon {
  id: string;
  addonType: AddonType;
  licenceId: string;
  cancelledAt: Date | undefined;
}

export interface Card extends SanitizedStripeSource {
  default: boolean;
}

export interface BillingModalData {
  subscription: SanitizedSubscription;
  licence: Licence | SanitizedLicence;
}

export type PaymentHistoryModalData = Pick<BillingModalData, 'subscription'>;
export type CancelSubscriptionRenewalModalData = {
  licence?: Licence | SanitizedLicence;
  subscription: SanitizedSubscription;
};
export type CancelLicenceRenewalModalData = {
  licence?: Licence | SanitizedLicence;
  subscription: SanitizedSubscription;
};
export type UnassignLicenceModalData = Pick<BillingModalData, 'licence'>;
export type DeleteLicenceModalData = Pick<BillingModalData, 'licence'>;
export type ManageLicenseModalData = BillingModalData & { billableSite?: HydratedBillableSite };
export type ManageSubscriptionModalData = Pick<BillingModalData, 'subscription'>;
export type EnableWorkflowsModalData = Pick<BillingModalData, 'licence'>;

// `null` means the modal is closed.
export interface BillingModalHandlers {
  onOpenPaymentHistoryModal: (data: null | PaymentHistoryModalData) => void;
  onOpenCancelSubscriptionRenewalModal: (data: null | CancelSubscriptionRenewalModalData) => void;
  onOpenCancelLicenceRenewalModal: (data: null | CancelLicenceRenewalModalData) => void;
  onOpenAllocateLicenceModal: (data: null | BillingModalData) => void;
  onOpenUnassignLicenceModal: (data: null | UnassignLicenceModalData) => void;
  onOpenDeleteLicenceModal: (data: null | DeleteLicenceModalData) => void;
  onOpenManageLicenseModal: (data: null | ManageLicenseModalData) => void;
  onOpenManageSubscriptionModal: (data: null | ManageSubscriptionModalData) => void;
  onEnableWorkflowsModal: (data: EnableWorkflowsModalData | null) => void;
  onOpenManageManualSubscriptionModal: (data: null | ManageSubscriptionModalData) => void;
}

export type RefreshableOnCloseFn = (refresh?: boolean) => void;

export interface PlanSelectOption extends ISelectOption {
  price?: PersistedStripePrice;
  subscriptionId?: string;
  count?: number;
  type?: string;
  licenseIds?: Array<string>;
  siteId?: string | null;
  renewalDateOption?: ISelectOption;
  disabled?: boolean; // allow disabled placeholder options
}

// renewalDate made optional and can be a number (epoch) or formatted string for display.
export interface LineItem {
  renewalDate?: number | string;
  id?: string;
  site: HydratedBillableSite | SanitizedBillingSite | Partial<Site> | undefined;
  selectedPlanOption?: PlanSelectOption;
  startDate?: string;
  endDate?: string;
  featureBasedCMSPlan?: string;
  licenceType?: string | null;
  licence?: ManageRecordLicence;
  type?: string;
  initialSelectedPlanOption?: PlanSelectOption;
  isNew?: boolean;
  note?: string;
}

export enum LineItemAggregationGroupType {
  NEW = 'new',
  EXISTING = 'existing',
  UPGRADE = 'upgrade',
  DOWNGRADE = 'downgrade',
}

export interface LineItemAggregationGroup {
  type?: LineItemAggregationGroupType;
  aggregations: Array<LineItemAggregation>;
}

export interface LineItemAggregation {
  quantity: number;
  selectedPlanOption?: PlanSelectOption;
  initialPlanOption?: PlanSelectOption;
}

type LicenceTypeNameKey = keyof typeof LicenceType;
export type LicenceTypeNameValue = `${LicenceType}`;

export const LICENSE_TYPE: Record<LicenceTypeNameKey, LicenceTypeNameValue> = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'enterprise',
  ENTERPRISE: 'unlimited',
  TRIAL_30_DAYS: 'trial-30-days',
  TRIAL_SYSADMIN: 'trial-sysadmin',
  /**
   * @deprecated Use TRIAL_30_DAYS instead
   */
  TRIAL_BASIC: 'trial-basic',
  VMSLITE: 'vmslite',
  VMSPLUS: 'vmsplus',
  VMSPREMIUM: 'vmspremium',
  VMSLITE_CMSLITE: 'vmslite_cmslite',
  VMSLITE_CMSPLUS: 'vmslite_cmsplus',
  VMSPLUS_CMSLITE: 'vmsplus_cmslite',
  VMSPLUS_CMSPLUS: 'vmsplus_cmsplus',
  VMSPREMIUM_CMSLITE: 'vmspremium_cmslite',
  VMSPREMIUM_CMSPLUS: 'vmspremium_cmsplus',
  VMSENTERPRISE: 'vmsenterprise',
  VMSENTERPRISE_CMSLITE: 'vmsenterprise_cmslite',
  VMSENTERPRISE_CMSPLUS: 'vmsenterprise_cmsplus',
  ENT_100: 'ent100',
} as const;
export interface WorkflowFreeTrialSchema {
  id: string;
  type: string;
  startDate: Date | string;
  expiryDate: Date | string;
  licenceType: LicenceType;
  initiatedByUserId: string;
  status: WorkflowFreeTrialStatus;
  cancelledDate?: Date | string;
}
export interface IWorkflowTrialDataResponse {
  status: WorkflowFreeTrialStatus;
  daysLeft: number;
  metadata: WorkflowFreeTrialSchema;
  settings?: WorkflowsTrialSettings;
}
export interface IProductIconBadgeConfig {
  badgeText: ProductIconBadgeText;
  badgeVariant?: 'primary' | 'sysAdmin';
}

export type PlanOption = {
  name: string;
  description: string;
  features: Array<string>;
  diffFromTypes?: Array<string>;
  type?: 'cmslite' | 'cmsplus' | null;
  mostPopular?: boolean;
};

type ManageLicenceExtension = {
  expiry: string;
  startDate?: string;
  billingCycle: string | null | undefined;
  type: 'stripe' | 'manual';
  licenceType: Licence['type'] | undefined;
};

export type ManageRecordLicence =
  | (Omit<Licence, 'type'> & ManageLicenceExtension)
  | (Omit<SanitizedLicence, 'type'> & ManageLicenceExtension);

export interface ManageTableRecord {
  licence?: ManageRecordLicence;
  site?: HydratedBillableSite;
}
